import FormButton from '@app/component/form/button/FormButton';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { MetaradarRefreshState, MetaradarRefreshStatus } from '@app/model/metaradar/MetaradarRefreshStatus';
import { MetaradarQueryKeys } from '@app/model/metaradar/service/MetaradarQueryKeys';
import { useMetaradarRefreshWatch } from '@app/site/secured/rateshopper/rates/provider/MetaradarRefreshWatchProvider';
import {
	useGetMetaradarRefreshStatusQuery,
	useTriggerMetaradarRefreshMutation,
} from '@app/model/metaradar/service/MetaradarServiceHooks';
import { ArrowPathIcon } from '@heroicons/react/16/solid';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import Utilities from '@util/Utilities';
import React, { useEffect, useRef } from 'react';
import { metaradarCooldownMessage } from './RefreshMetaradarButton.utilities';

const RefreshMetaradarButton: React.FunctionComponent<React.PropsWithChildren<{ companyHotelId: number }>> = ({ companyHotelId, children }) => {
	const toaster = useToaster();
	const queryClient = useQueryClient();
	// The end of a refresh shows a toast on any page through this watch, not on this tab (ROO2-232).
	const refreshWatch = useMetaradarRefreshWatch();
	const statusQuery = useGetMetaradarRefreshStatusQuery(companyHotelId);
	const handledRequestId = useRef<number | undefined>(undefined);
	const updatingCompanyHotelId = useRef<number | undefined>(undefined);
	const status = statusQuery.data;
	const executionState = status?.executionState;
	const isInProgress = status?.status === MetaradarRefreshState.IN_PROGRESS;
	const isCooldown = status?.status === MetaradarRefreshState.COOLDOWN;

	const refreshMutation = useTriggerMetaradarRefreshMutation(companyHotelId);
	const isStarting = useIsMutating({ mutationKey: MetaradarQueryKeys.refreshStatus(companyHotelId) }) > 0;

	useEffect(() => {
		if (status?.status === MetaradarRefreshState.IN_PROGRESS) updatingCompanyHotelId.current = companyHotelId;
		if (!executionState) {
			// A running refresh can show before its execution details: watch it for the hotel until the id comes.
			if (status?.status === MetaradarRefreshState.IN_PROGRESS) refreshWatch.watch(companyHotelId, undefined);
			if (status?.status === MetaradarRefreshState.READY && updatingCompanyHotelId.current === companyHotelId) {
				updatingCompanyHotelId.current = undefined;
				queryClient.invalidateQueries({ queryKey: MetaradarQueryKeys.companyHotel(companyHotelId) });
			}
			return;
		}

		if (!executionState.isFinished) {
			refreshWatch.watch(companyHotelId, executionState.requestId);
			return;
		}
		if (handledRequestId.current === executionState.requestId) {
			return;
		}

		handledRequestId.current = executionState.requestId;
		queryClient.invalidateQueries({ queryKey: MetaradarQueryKeys.companyHotel(companyHotelId) });
	}, [companyHotelId, executionState, status?.status, queryClient, refreshWatch]);

	const triggerRefresh = () => {
		refreshMutation.mutate({ companyHotelId }, {
			onSuccess: data => {
				const refreshStatus = new MetaradarRefreshStatus(data);
				if (refreshStatus.status === MetaradarRefreshState.COOLDOWN) {
					toaster.error('Metaradar', metaradarCooldownMessage(refreshStatus.retryAfterSeconds ?? 1));
				} else if (refreshStatus.status === MetaradarRefreshState.IN_PROGRESS) {
					refreshWatch.watch(companyHotelId, refreshStatus.executionState?.requestId);
					toaster.info('Metaradar', 'Refresh started. Results may take a few minutes.');
				}
			},
			onError: () => toaster.error('Metaradar', 'Metaradar could not be refreshed at the moment. Please try again later!'),
		});
	};

	const progress = executionState?.progressPercentage ?? 0;
	return <div className="flex flex-col items-end gap-1">
		<div className="flex flex-wrap justify-end items-center gap-2">
			{children}
			<FormButton
				state="primary"
				onClick={triggerRefresh}
				disabled={status?.status !== MetaradarRefreshState.READY || isStarting || !statusQuery.isFetchedAfterMount || statusQuery.isError}
				extraClassName="py-1.5 relative"
				icon={<ArrowPathIcon className={Utilities.classNames('w-4 h-4 mr-2 mt-0.5', isInProgress ? 'animate-spin' : '')} />}
			>
				{isInProgress ? 'Updating…' : isStarting ? 'Starting…' : 'Refresh'}
				{isInProgress && <div className="overflow-hidden absolute h-1 text-xs flex rounded bg-gray-200 -bottom-[1px] w-full">
					<div className="transition-all bg-gradient-to-r from-blue-200 to-blue-500" style={{ width: `${progress}%` }} />
				</div>}
			</FormButton>
		</div>
		{isInProgress && <p role="status" className="text-xs text-gray-500">Refresh started. Results may take a few minutes.</p>}
		{isCooldown && <p role="status" className="text-xs text-gray-500">{metaradarCooldownMessage(status.retryAfterSeconds ?? 1)}</p>}
		{statusQuery.isError && <p role="status" className="text-xs text-gray-500">Refresh status is unavailable. <button type="button" className="underline" onClick={() => statusQuery.refetch()}>Try again</button></p>}
	</div>;
};

export default RefreshMetaradarButton;
